import AWS from 'aws-sdk';
import { nanoid } from 'nanoid';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import Handlebars from 'handlebars/dist/cjs/handlebars';
import sanitizeHtml from 'sanitize-html';
import { createFile, genSignedUrl } from '../api';
import * as cheerio from 'cheerio';


const stateAbbreviations = [ 'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY' ];

const monthsShort = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];
const monthsLong = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ];

const chartColors = [
	'rgba(0, 115, 177, 0.85)',
	'rgba(255, 122, 89, 0.85)',
	'rgba(0, 115, 177, 0.6)',
	'rgba(255, 122, 89, 0.6)',
	'rgba(0, 115, 177, 0.7)',
	'rgba(255, 122, 89, 0.7)',
	'rgba(0, 115, 177, 0.45)',
	'rgba(255, 122, 89, 0.45)',
	'rgba(0, 115, 177, 0.55)',
	'rgba(255, 122, 89, 0.55)',
	'rgba(0, 115, 177, 0.3)',
	'rgba(255, 122, 89, 0.3)',
	'rgba(0, 115, 177, 0.4)',
	'rgba(255, 122, 89, 0.4)',
	'rgba(0, 115, 177, 0.15)',
	'rgba(255, 122, 89, 0.15)'
];

const cleanNumber = ( num ) => {
    if (typeof num === 'string') {
        // Remove any non-numeric characters except for the decimal point
        num = num.replace( /[^0-9.-]+/g, '' );
    }
    // Convert the cleaned string to a number
    return parseFloat( num );
};

const formatShorthand = ( num, digits ) => {
	num = cleanNumber( num );
    const lookup = [
        { value: 1, symbol: "" },
        { value: 1e3, symbol: "k" },
        { value: 1e6, symbol: "M" },
        { value: 1e9, symbol: "B" },
        { value: 1e12, symbol: "T" }
    ];
	
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    let item = lookup.slice().reverse().find(function(item) {
        return num >= item.value;
    });
    return ( item && num > 0 ) ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0";
}

const formatCurrencyShort = ( amount ) => {
	if ( amount == null || isNaN( amount ) ) return '$0';
  
	const abs = Math.abs( amount );
	const sign = amount < 0 ? '-' : '';
  
	if ( abs >= 1_000_000_000 ) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
	if ( abs >= 1_000_000 ) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
	if ( abs >= 1_000 ) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  
	return `${sign}$${abs.toFixed(0)}`;
}

const formatName = ( name ) => {
	if ( typeof name !== 'string' ) return name;
  
	if ( name.length >= 3 && name === name.toUpperCase() ) {
	  return name
		.toLowerCase()
		.split(' ')
		.map( word => word.charAt( 0 ).toUpperCase() + word.slice( 1 ) )
		.join( ' ' );
	}
  
	return name;
}

const formatPhone = ( p ) => {
    let ret;

	if ( !p || !p.length ) {
		return ret;
	}
	
	// strip all the non-digits away
	let phone = p.replace( /\D/g, '' );
	
	// strip country code
	if ( phone.length > 10 ) {
		phone = phone.replace( /^1/, '' );
	}

	// expecting US numbers
	if ( phone.length === 10 ) {
		ret = phone.replace( /(\d\d\d)(\d\d\d)(\d\d\d\d)/, "$1-$2-$3" ); 
	}

	return ( ret );
}

const formatDate = ( d ) => {
	let dd = d.getDate();
	let mm = d.getMonth() + 1;
	const yyyy = d.getFullYear();

	if ( dd < 10 ) {
		dd = '0' + dd;
	}

	if ( mm < 10 ) {
		mm = '0' + mm;
	}

	return ( yyyy + '-' + mm + '-' + dd );
}

const formatTime = ( d ) => {
	let hh = d.getHours();
	let mm = d.getMinutes();
	
	if ( hh < 10 ) {
		hh = '0' + hh;
	}

	if ( mm < 10 ) {
		mm = '0' + mm;
	}

	return ( hh + ':' + mm );
}

const singleSelectQuickFilter = ( value ) => {
	if ( !value || !value.length ) {
		return null;
	}
	return ( params ) => {
		return ( params.value && params.value.length && params.value.toLowerCase().includes( value.toLowerCase() ) );
	};
};

const storeMedia = async ( aid, uid, name, blob, mimeType ) => {
	const bucket = 'tf-media';

	try {
        const s3 = await genSignedUrl( aid, uid, bucket + '/' + aid, name, mimeType );
		const resp = await axios.put( s3.url, blob, {
			headers: {
				'Content-Type': mimeType
			}
		} );
		console.log( 'storeMedia resp: ', resp );
        return name;
    } catch( e ) {
		console.log( 'storeMedia Error:', e );
	}

	return null;
};

const storeEmailImage = async ( aid, uid, name, b64img, mimeType ) => {
	const bucket = 'tf-email-images';
	let url;

	try {
		const s3 = await genSignedUrl( aid, uid, bucket + '/' + aid, name, mimeType, true );
		const resp = await axios.put( s3.url, base64ToBlob( b64img, mimeType ), {
			headers: {
				'Content-Type': mimeType
			}
		} );
        url = `https://${bucket}.s3.amazonaws.com/${aid}/${name}`;
		console.log( aid + ': storeEmailImage for ' + name + ' is done: ', resp );
    } catch( e ) {
		console.log( aid + ': Error storing email image ' + name + ' - ', e );
	}

	return url;
}

const extractImages = async ( aid, uid, body ) => {
	const $ = cheerio.load( body, null, false );
	
	for ( let img of $( 'img' ).toArray() ) {
		const src = $( img ).attr( 'src' );
		if ( src.startsWith( 'data:image' ) ) {
			const [ meta, b64 ] = src.split( ',' );
			const mimeType = meta.split( /[:;]/ )[1];
			const name = createId( 'img' ) + getExtensionFromMimeType( mimeType );
			const url = await storeEmailImage( aid, uid, name, b64, mimeType );
			$(img).attr( 'src', url );
		}
	}

	return $.html();
}

const base64ToBlob = ( base64, mimeType ) => {
	// const splitDataURI = base64.split( ',' );
	// const byteString = atob( splitDataURI[1] );
	const byteString = atob( base64 );
	const arrayBuffer = new ArrayBuffer( byteString.length );
	const int8Array = new Uint8Array( arrayBuffer );
  
	for ( let i = 0; i < byteString.length; i++ ) {
	  int8Array[i] = byteString.charCodeAt( i );
	}
  
	return new Blob( [int8Array], { type: mimeType } );
}

const createId = ( prefix ) => {
	return prefix + '_' + nanoid();
}

const getExtensionFromMimeType = ( mimeType ) => {
    const mimeTypes = {
        'image/jpeg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/webp': '.webp',
        // Add other MIME types and extensions as needed
    };
    return mimeTypes[mimeType] || '.jpg'; // Default to .jpg if unknown
}

const getObjectName = ( oid ) => {
	const prefix = oid.split( '_' )[0];
	let obj;

	switch ( prefix ) {
		case 'con':
			obj = 'contact';
			break;

		case 'comm':
			obj = 'comm';
			break;

		case 'msg':
			obj = 'message';
			break;

		case 'evnt':
			obj = 'event';
			break;

		case 'file':
			obj = 'file';
			break;
			
		case 'gift':
			obj = 'gift';
			break;

		case 'gp':
			obj = 'gameplan';
			break;
			
		case 'grp':
			obj = 'list';
			break;

		case 'lo':
			obj = 'leadopp';
			break;

		case 'loan':
			obj = 'loan';
			break;

		case 'note':
			obj = 'note';
			break;

		case 'offr':
			obj = 'offer';
			break;

		case 'pb':
			obj = 'playbook';
			break;

		case 'revu':
			obj = 'review';
			break;

		case 'ro':
			obj = 'referredout';
			break;

		case 'task':
			obj = 'task';
			break;

		case 'tmpl':
			obj = 'template';
			break;

		default:
	}

	return obj;
}

const happeningToday = ( today, d ) => {
	return ( today.getTime() === d.getTime() );
}

const happeningTomorrow = ( today, d ) => {
	const tom = today.getTime() + ( 1000 * 60 * 60 * 24 );
	return ( tom === d.getTime() );
}

const happeningWeek = ( today, d ) => {
	const start = today.getTime() + ( ( 1000 * 60 * 60 * 24 ) * 2 );
	const stop = start + ( ( 1000 * 60 * 60 * 24 ) * 6 );
	return ( start <= d.getTime() && stop >= d.getTime() );
}

const isToday = ( dateString, ignoreYear = false ) => {
	const givenDate = new Date( dateString.match( 'T' ) ? dateString : dateString + 'T12:00:00' );
	const today = new Date();
	
	if ( ignoreYear ) {
		givenDate.setFullYear( today.getFullYear() );
	}

	const isSameYear = givenDate.getFullYear() === today.getFullYear();
	const isSameMonth = givenDate.getMonth() === today.getMonth();
	const isSameDay = givenDate.getDate() === today.getDate();
	
	return isSameYear && isSameMonth && isSameDay;
}

const isTomorrow = ( dateString, ignoreYear = false ) => {
	const givenDate = new Date( dateString.match( 'T' ) ? dateString : dateString + 'T12:00:00' );
	const tomorrow = new Date();
	tomorrow.setDate( tomorrow.getDate() + 1 );
	
	if ( ignoreYear ) {
		givenDate.setFullYear( tomorrow.getFullYear() );
	}
	
	const isSameYear = ( ignoreYear ) ? true : givenDate.getFullYear() === tomorrow.getFullYear();
	const isSameMonth = givenDate.getMonth() === tomorrow.getMonth();
	const isSameDay = givenDate.getDate() === tomorrow.getDate();
	
	return isSameYear && isSameMonth && isSameDay;
}

const isThisWeek = ( dateString, ignoreYear = false ) => {
	const givenDate = new Date( dateString.match( 'T' ) ? dateString : dateString + 'T12:00:00' );
	const today = new Date();
  
	const startOfWeek = new Date( today.setDate( today.getDate() - today.getDay() ) );
  
	const endOfWeek = new Date( startOfWeek );
	endOfWeek.setDate( startOfWeek.getDate() + 6 );
  
	if ( ignoreYear ) {
		givenDate.setFullYear( endOfWeek.getFullYear() );
	}
	
	startOfWeek.setHours( 0, 0, 0, 0 );
	endOfWeek.setHours( 23, 59, 59, 999 );
	givenDate.setHours( 0, 0, 0, 0 );
  
	return ( givenDate >= startOfWeek && givenDate <= endOfWeek );
}

const isNextWeek = ( dateString, ignoreYear = false ) => {
	const givenDate = new Date( dateString.match( 'T' ) ? dateString : dateString + 'T12:00:00' );
	const today = new Date();
  
	const currentWeekStart = new Date( today.setDate( today.getDate() - today.getDay() ) );
  
	const nextWeekStart = new Date( currentWeekStart );
	nextWeekStart.setDate( currentWeekStart.getDate() + 7 );
  
	const nextWeekEnd = new Date( nextWeekStart );
	nextWeekEnd.setDate( nextWeekStart.getDate() + 6 );
  
	if ( ignoreYear ) {
		givenDate.setFullYear( nextWeekEnd.getFullYear() );
	}
	
	nextWeekStart.setHours( 0, 0, 0, 0 );
	nextWeekEnd.setHours( 23, 59, 59, 999 );
	givenDate.setHours( 0, 0, 0, 0 );
  
	return ( givenDate >= nextWeekStart && givenDate <= nextWeekEnd );
}

const isThisMonth = ( dateString, ignoreYear = false ) => {
	const givenDate = new Date( dateString.match( 'T' ) ? dateString : dateString + 'T12:00:00' );
	const today = new Date();
  
	if ( ignoreYear ) {
		givenDate.setFullYear( today.getFullYear() );
	}
	
	const isSameYear = givenDate.getFullYear() === today.getFullYear();
	const isSameMonth = givenDate.getMonth() === today.getMonth();
  
	return isSameYear && isSameMonth;
}

const isNextMonth = ( dateString, ignoreYear = false ) => {
	const givenDate = new Date( dateString.match( 'T' ) ? dateString : dateString + 'T12:00:00' );
	const today = new Date();
  
	const currentYear = today.getFullYear();
	const currentMonth = today.getMonth();
  
	const nextMonth = ( currentMonth + 1 ) % 12;
	const nextMonthYear = ( currentMonth === 11 ) ? currentYear + 1 : currentYear;
  
	if ( ignoreYear ) {
		givenDate.setFullYear( nextMonthYear.getFullYear() );
	}
	
	// Check if the given date's month and year match the next month's
	const isSameYear = givenDate.getFullYear() === nextMonthYear;
	const isSameMonth = givenDate.getMonth() === nextMonth;
  
	return ( isSameYear && isSameMonth );
}

const launchPhoneBurner = async ( aid, uid, cids, contacts, burner ) => {
	const axios = require( 'axios' );
	let list = [];

	for ( let i = 0; i < cids.length; i++ ) {
        const c = contacts.find( f => f.cid === cids[i] );
        let pb = {
            first_name: c.firstname,
            last_name: c.lastname,
            lead_id: c.cid,
			external_crm_data: [
                {
                    crm_id: c.cid,
                    crm_name: "ThirdFloor"
                }
            ],
            custom_fields: [
                // {
                //     name: "Call Notes",
                //     value: "Buy side trx: " + r.buyside + " - List side trx: " + r.listside
                // }
            ]
        }
        if ( c.phoneMobile && c.phoneMobile.length ) {
            pb.phone = c.phoneMobile;
            pb.phone_type = 3;
        }
        if ( c.phone && c.phone.length ) {
            if ( !c.phoneMobile || !c.phoneMobile.length ) {
                pb.phone = c.phone.replace( /\D/g, '' );
                pb.phone_type = 5;
            } else {
                pb.addition_phone = [
                    {
                        number: c.phone,
                        phone_type: 5
                    }
                ]
            }
        }
        if ( c.email && c.email.length ) {
            pb.email = c.email;
        }
        list.push( pb );
    }

    let payload = {
		contacts: list,
        dialing_disposition_set_id: parseInt( burner.auth.dial ),
        answer_disposition_set_id: parseInt( burner.auth.answer ),
		allow_duplicates: 0,
        preview_mode: 1,
        custom_data: {
			aid: aid,
			uid: uid
		},
        callbacks: [
            {
                callback_type: "api_calldone",
                callback: `https://api-dev.thirdfloor.ai/dev/phoneburner?aid=${aid}&uid=${uid}`
            },
            {
                callback_type: "api_callbegin",
                callback: "https://api-dev.thirdfloor.ai/null"
            }
        ]
    };
        
    const resp = await axios( 'https://www.phoneburner.com/rest/1/dialsession', {
		method: 'POST',
		// mode: 'no-cors',
        headers: {
			'Accept': 'application/json',
			'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + burner.auth.token
		},
		data: JSON.stringify( payload )
	} );
    
	if ( resp.status === 201 ) {
		const redir = resp.data.dialsessions.redirect_url;
	    window.open( redir, "popup", "width:800,height:600" );
	} else {
		console.log( 'not 201!', resp );
	}
}

const generatePdf = async ( aid, uid, name, body, params, saveLocally, contacts, openImmediately, orientation = 'portrait' ) => {
	const filename = render( name, params, contacts );
	const opts = {
		filename: filename,
		html2canvas: { scale: 3, allowTaint: true, useCORS: true, dpi: 300 },
		jsPDF: { unit: 'in', format: 'letter', orientation: orientation, hotfixes: [ 'px_scaling' ] }
	}
	
	const rBody = render( body, params, contacts );
	const pdfBlob = await html2pdf().set( opts ).from( rBody ).toPdf().output( 'blob' );

	const n = createId( 'file' ) + '.pdf';
    await storeMedia( aid, uid, n, pdfBlob, 'application/pdf' );
 
    const file = {
        mediaUrl: n,
        name: filename,
        cid: params.contact.cid
    }
	if ( params.loan ) {
		file.lid = params.loan.details.lid;
	}

	await createFile( aid, uid, file );

	if ( saveLocally ) {
    	await html2pdf().set( opts ).from( rBody ).save();
	}

	if ( openImmediately ) {
		await html2pdf().set( opts ).from( rBody ).output( 'dataurlnewwindow' );
	}

	return { name: file.name, fid: file.mediaUrl };
}

const addCrossoriginToImages = ( htmlString ) => {
    // Parse the HTML string into a DOM object
    const parser = new DOMParser();
    const doc = parser.parseFromString( htmlString, 'text/html' );

    // Select all img elements and add crossorigin="anonymous"
    const images = doc.querySelectorAll( 'img' );
    images.forEach( ( img ) => {
		if ( !img.hasAttribute( 'crossorigin' ) ) {
			img.setAttribute( 'crossorigin', '*' );
		}
    });

    // Serialize the DOM back into an HTML string
    return doc.documentElement.innerHTML;
};

const render = ( content, params, contacts ) => {
	const t = Handlebars.compile( content );
    
	const rateExtreme = ( arr, program, options, pickFn ) => {
		// guard rails
		if (!Array.isArray(arr) || !program) return '';

		const { period = 'week', end = null } = options.hash || {};
	  
		// Resolve end date (default: latest in data)
		const endDate = end ? new Date(end) :
		  new Date(arr.reduce((max, row) => (row.date > max ? row.date : max), ''));
	  
		if (isNaN(endDate)) return '';
	  
		// Compute start date based on period
		const startDate = new Date(endDate);
		switch (period.toLowerCase()) {
		  case 'year':  startDate.setFullYear(startDate.getFullYear() - 1); break;
		  case 'month': startDate.setMonth(startDate.getMonth() - 1);       break;
		  default:      startDate.setDate(startDate.getDate() - 7);         // week
		}
	  
		// Filter rows in the program & window
		const rows = arr.filter(r =>
		  r.program === program &&
		  new Date(r.date) >= startDate &&
		  new Date(r.date) <= endDate
		);
		if (rows.length === 0) return '';
	  
		// Pick min or max on numeric value of 'rate'
		const result = pickFn(...rows.map(r => parseFloat(r.rate))); // strip %
	  
		// Return with percent sign
		return `${result.toFixed(2)}%`;
	};

	Handlebars.registerHelper( 'gte', function ( a, b ) {
        const aa = ( a ) ? Number( a ) : 0;
        const bb = ( b ) ? Number( b ) : 0;
        return ( aa >= bb );
    } );
    Handlebars.registerHelper( 'isYes', function ( value ) {
        return ( value === 'Yes' );
    } );
    Handlebars.registerHelper( 'isRefi', function ( value ) {
        return value && value.match( /refi/i );
    } );
    Handlebars.registerHelper( 'textMatch', function ( a, b ) {
        return ( a === b );
    } );
    Handlebars.registerHelper( 'textMismatch', function ( a, b ) {
        return ( a !== b );
    } );
    Handlebars.registerHelper( 'formatMoney', function ( value, forceDecimals = false ) {
        if ( !value ) {
            return '';
        }
        const sanitizedInput = ( typeof( value ) === 'string' ) ? value.replace( /[\$,]/g, '' ) : value;
        const val = parseFloat( sanitizedInput );

        // Check if the value is a number
        if ( isNaN( val ) ) {
            return '';
        }

        // Check if the input has decimal places
        const hasDecimals = ( val % 1 ) !== 0;

        // Create a formatter
        const formatter = new Intl.NumberFormat( 'en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: forceDecimals || hasDecimals ? 2 : 0,
            maximumFractionDigits: forceDecimals || hasDecimals ? 2 : 0,
        } );

        return formatter.format( val );
    } );
    Handlebars.registerHelper( 'formatPercentage', function ( value ) {
        if ( !value ) {
            return '';
        }
        const sanitizedInput = ( typeof( value ) === 'string' ) ? value.replace(/%/g, '') : value;
        const val = parseFloat( sanitizedInput );

        // Check if the value is a number
        if ( isNaN( val ) ) {
            return '';
        }

        // Check if the input is already a percentage (i.e., it's greater than 1)
        const isDecimal = Math.abs( val ) <= 1;

        // Create a formatter
        const formatter = new Intl.NumberFormat( 'en-US', {
            style: 'percent',
            minimumFractionDigits: 0, // isDecimal ? 3 : 3,
            maximumFractionDigits: 3 // isDecimal ? 3 : 3,
        } );

        return formatter.format( isDecimal ? val : val / 100 );
    } );
	Handlebars.registerHelper( 'dropDecimals', function ( value ) {
        const num = parseFloat( value );

		// If it's not a valid number, return an empty string
		if ( isNaN( num ) ) {
			return '';
		}

		// Return the integer part of the number by using Math.floor
		return Math.floor( num );
    } );
	Handlebars.registerHelper( 'formatName', function ( name ) {
        if ( typeof name !== 'string' ) return name;
          
        if ( name.length >= 3 && name === name.toUpperCase() ) {
            return name
                .toLowerCase()
                .split(' ')
                .map( word => word.charAt( 0 ).toUpperCase() + word.slice( 1 ) )
                .join( ' ' );
        }
          
        return name;
    } );
	Handlebars.registerHelper( 'tod', function () {
		const currentHour = new Date().getHours(); // Gets the current hour (0-23)

		if ( currentHour < 12 ) {
			return "morning";
		} else if ( currentHour < 18 ) {
			return "afternoon";
		} else {
			return "evening";
		}
	} );
	Handlebars.registerHelper( 'formatMonthDay', function ( value ) {
		if ( !value || !value.length ) {
			return '';
		}
		const months = [ "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December" ];
		const d = new Date( value.match( 'T' ) ? value : value + 'T12:00:00' );
		const day = d.getDate();
		const monthIndex = d.getMonth();
		let suffix = '';
		if ( day > 3 && day < 21 ) {
			suffix = 'th';
		} else {
			switch ( day % 10 ) {
				case 1:
					suffix = 'st';
					break;
				case 2:
					suffix = 'nd';
					break;
				case 3:
					suffix = 'rd';
					break;
				default:
					suffix = 'th';
			}
		}
	
		return `${months[monthIndex]} ${day}${suffix}`;
	} );
	Handlebars.registerHelper( 'formatYearMonthDay', function ( value ) {
		if ( !value || !value.length ) {
			return '';
		}
		const months = [ "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December" ];
		const d = new Date( value.match( 'T' ) ? value : value + 'T12:00:00' );
		const day = d.getDate();
		const monthIndex = d.getMonth();
		let suffix = '';
		if ( day > 3 && day < 21 ) {
			suffix = 'th';
		} else {
			switch ( day % 10 ) {
				case 1:
					suffix = 'st';
					break;
				case 2:
					suffix = 'nd';
					break;
				case 3:
					suffix = 'rd';
					break;
				default:
					suffix = 'th';
			}
		}
	
		return `${months[monthIndex]} ${day}${suffix}, ${d.getFullYear()}`;
	} );
	Handlebars.registerHelper( 'formatDayDate', function ( value ) {
        if ( !value || !value.length ) {
            return '';
        }
        const days = [ "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday" ];
        const months = [ "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec" ];
        
        const date = new Date( value );
        const dayOfWeek = days[date.getDay()];
        const month = months[date.getMonth()];
        const day = date.getDate();
        const ordinalSuffix = day % 10 === 1 && day !== 11 ? "st" : day % 10 === 2 && day !== 12 ? "nd" : day % 10 === 3 && day !== 13 ? "rd" : "th";
        
        return `${dayOfWeek}, ${month} ${day}${ordinalSuffix}`;
    } );
    Handlebars.registerHelper( 'formatDateDots', function ( dateString ) {
		if ( !dateString || !dateString.length ) {
			return '';
		}

		const date = new Date( dateString );
	  
		const month = date.getMonth() + 1;
		const day = date.getDate();
		const year = date.getFullYear();
	  
		return `${month}.${day}.${year}`;
	} );
	Handlebars.registerHelper( 'today', function () {
		const date = new Date();
		const year = date.getFullYear();
		const month = String( date.getMonth() + 1 ).padStart( 2, '0' );
		const day = String( date.getDate() ).padStart( 2, '0' );
		return `${year}${month}${day}`;
	} );
	Handlebars.registerHelper( 'todayPretty', function () {
		const options = { year: 'numeric', month: 'long', day: 'numeric' };
		const today = new Date();
		return today.toLocaleDateString( 'en-US', options );
	} );
	Handlebars.registerHelper( 'birthday', function ( value ) {
        if ( !value || value.length === 0) return '';
        const c = contacts[value.cid];
        return ( c.birthday ) ? c.birthday : '';
    } );
    Handlebars.registerHelper( 'clientPartnerFirstNames', function ( value ) {
		if ( value.length === 0) return '';

		const list = [];
		value.forEach( b => {
			const c = contacts[b.cid];
			list.push( {
				firstname: formatName( c.firstname ),
				lastname: formatName( c.lastname )
			} );

			if ( c.firstnamePartner1 && c.firstnamePartner1.length ) {
				list.push( {
					firstname: formatName( c.firstnamePartner1 ),
					lastname: formatName( c.lastnamePartner1 )
				} );
			}
			if ( c.firstnamePartner2 && c.firstnamePartner2.length ) {
				list.push( {
					firstname: formatName( c.firstnamePartner2 ),
					lastname: formatName( c.lastnamePartner2 )
				} );
			}
		} );
		console.log( 'value vs list: ', value, list );
		
		const firstNames = list.map( name => name.firstname );

		// Handle the formatting based on the number of first names
		if ( firstNames.length === 1 ) {
			// Only one first name
			return firstNames[0];
		} else {
			// Multiple first names, join all but the last with commas, and add 'and' before the last name
			const allButLast = firstNames.slice( 0, -1 ).join( ', ' );
			const last = firstNames[firstNames.length - 1];
			return `${allButLast} and ${last}`;
		}
	} );
	Handlebars.registerHelper( 'borrowerFirstNames', function ( value ) {
		if ( value.length === 0) return '';

		const list = value.map( m => {
			const c = contacts[m.cid];
			return {
				firstname: formatName( c.firstname ),
				lastname: formatName( c.lastname ),
				label: m.label
			}
		} );
		list.sort( ( a, b ) => {
			if ( a.label === 'Primary' ) {
				return -1;		
			}
			if ( b.label === 'Primary' ) {
				return 1;		
			}
			return 0;
		} );
		console.log( 'value vs list: ', value, list );
		
		const firstNames = list.map( name => name.firstname );

		// Handle the formatting based on the number of first names
		if ( firstNames.length === 1 ) {
			// Only one first name
			return firstNames[0];
		} else {
			// Multiple first names, join all but the last with commas, and add 'and' before the last name
			const allButLast = firstNames.slice( 0, -1 ).join( ', ' );
			const last = firstNames[firstNames.length - 1];
			return `${allButLast} and ${last}`;
		}
	} );
	Handlebars.registerHelper( 'borrowerLastNames', function ( value ) {
		if ( value.length === 0) return '';

		const list = value.map( m => {
			const c = contacts[m.cid];
			return {
				firstname: formatName( c.firstname ),
				lastname: formatName( c.lastname ),
				label: m.label
			}
		} );
		list.sort( ( a, b ) => {
			if ( a.label === 'Primary' ) {
				return -1;		
			}
			if ( b.label === 'Primary' ) {
				return 1;		
			}
			return 0;
		} );
		
		const lastNames = list.map( name => name.lastname );

		// Handle the formatting based on the number of first names
		if ( lastNames.length === 1 ) {
			// Only one first name
			return lastNames[0];
		} else {
			// Multiple first names, join all but the last with commas, and add 'and' before the last name
			const list = [];
			const seen = new Set();
			lastNames.forEach( ln => {
				if ( !seen.has( ln ) ) {
					list.push( ln );
					seen.add( ln );
				}
			} );
			return list.join( '/' );
		}
	} );
	Handlebars.registerHelper( 'partnerFirstNames', function ( value ) {
		if ( value.length === 0) return '';
		const list = [];

		value.forEach( m => {
			const c = contacts[m.cid];
			list.push( {
				firstname: formatName( c.firstname ),
				lastname: formatName( c.lastname ),
				label: 'Primary'
			} );
			if ( c.firstnamePartner1 ) {
				list.push( {
					firstname: formatName( c.firstnamePartner1 ),
					lastname: formatName( c.lastnamePartner1 ),
					label: 'Partner1'
				} );
			}
			if ( c.firstnamePartner2 ) {
				list.push( {
					firstname: formatName( c.firstnamePartner2 ),
					lastname: formatName( c.lastnamePartner2 ),
					label: 'Partner2'
				} );
			}
		} );
		list.sort( ( a, b ) => {
			if ( a.label === 'Primary' ) {
				return -1;		
			}
			if ( b.label === 'Primary' ) {
				return 1;		
			}
			return a.label.localeCompare( b.label );
		} );
		
		const firstNames = list.map( name => name.firstname );

		// Handle the formatting based on the number of first names
		if ( firstNames.length === 1 ) {
			// Only one first name
			return firstNames[0];
		} else {
			// Multiple first names, join all but the last with commas, and add 'and' before the last name
			const allButLast = firstNames.slice( 0, -1 ).join( ', ' );
			const last = firstNames[firstNames.length - 1];
			return `${allButLast} and ${last}`;
		}
	} );
	Handlebars.registerHelper( 'partnerLastNames', function ( value ) {
		if ( value.length === 0) return '';
		const list = [];

		value.forEach( m => {
			const c = contacts[m.cid];
			list.push( {
				firstname: formatName( c.firstname ),
				lastname: formatName( c.lastname ),
				label: 'Primary'
			} );
			if ( c.firstnamePartner1 ) {
				list.push( {
					firstname: formatName( c.firstnamePartner1 ),
					lastname: formatName( c.lastnamePartner1 ),
					label: 'Partner'
				} );
			}
			if ( c.firstnamePartner2 ) {
				list.push( {
					firstname: formatName( c.firstnamePartner2 ),
					lastname: formatName( c.lastnamePartner2 ),
					label: 'Partner'
				} );
			}
		} );
		list.sort( ( a, b ) => {
			if ( a.label === 'Primary' ) {
				return -1;		
			}
			if ( b.label === 'Primary' ) {
				return 1;		
			}
			return 0;
		} );
		
		const lastNames = list.map( name => name.lastname );

		// Handle the formatting based on the number of first names
		if ( lastNames.length === 1 ) {
			// Only one first name
			return lastNames[0];
		} else {
			// Multiple first names, join all but the last with commas, and add 'and' before the last name
			const list = [];
			const seen = new Set();
			lastNames.forEach( ln => {
				if ( ln?.length && !seen.has( ln ) ) {
					list.push( ln );
					seen.add( ln );
				}
			} );
			return list.join( '/' );
		}
	} );
	Handlebars.registerHelper( 'partnerFullNames', function ( value ) {
		if ( value.length === 0) return '';

		const list = [];
		value.forEach( m => {
			const c = contacts[m.cid];
			list.push( {
				firstname: formatName( c.firstname ),
				lastname: formatName( c.lastname ),
				label: 'Primary'
			} );
			if ( c.firstnamePartner1 ) {
				list.push( {
					firstname: formatName( c.firstnamePartner1 ),
					lastname: formatName( c.lastnamePartner1 ),
					label: 'Partner'
				} );
			}
			if ( c.firstnamePartner2 ) {
				list.push( {
					firstname: formatName( c.firstnamePartner2 ),
					lastname: formatName( c.lastnamePartner2 ),
					label: 'Partner'
				} );
			}
		} );
		list.sort( ( a, b ) => {
			if ( a.label === 'Primary' ) {
				return -1;		
			}
			if ( b.label === 'Primary' ) {
				return 1;		
			}
			return 0;
		} );
		
		// Group names by last name
		const groups = list.reduce( ( acc, { firstname, lastname } ) => {
			acc[lastname] = acc[lastname] || [];
			acc[lastname].push( firstname );
			return acc;
		}, {} );
		
		// Format each group
		const formattedGroups = Object.entries( groups ).map( ( [ lastname, firstnames ] ) => {
			if ( firstnames.length === 1 ) {
				// Single name in the group
				return `${firstnames[0]} ${lastname}`;
			} else {
				// Multiple names, join all but the last with commas, and add 'and' before the last name
				const allButLast = firstnames.slice( 0, -1 ).join( ', ' );
				const last = firstnames[firstnames.length - 1];
				return `${allButLast} and ${last} ${lastname}`;
			}
		} );

		// Join groups with 'and' for the final output
		if ( formattedGroups.length === 1 ) {
			return formattedGroups[0];
		} else {
			const lastGroup = formattedGroups.pop();
			return `${formattedGroups.join(', ')} and ${lastGroup}`;
		}
	} );
    Handlebars.registerHelper( 'borrowerPreferredFirstNames', function ( value ) {
		if ( !value || value.length === 0 ) return '';

		const list = value.map( m => {
			const c = contacts[m.cid];
			return {
				firstname: formatName( ( c.namePreferred && c.namePreferred.length ) ? c.namePreferred : c.firstname ),
				lastname: formatName( c.lastname ),
				label: m.label
			}
		} );
		list.sort( ( a, b ) => {
			if ( a.label === 'Primary' ) {
				return -1;		
			}
			if ( b.label === 'Primary' ) {
				return 1;		
			}
			return 0;
		} );
		
		const firstNames = list.map( name => name.firstname );

		// Handle the formatting based on the number of first names
		if ( firstNames.length === 1 ) {
			// Only one first name
			return firstNames[0];
		} else {
			// Multiple first names, join all but the last with commas, and add 'and' before the last name
			const allButLast = firstNames.slice( 0, -1 ).join( ', ' );
			const last = firstNames[firstNames.length - 1];
			return `${allButLast} and ${last}`;
		}
	} );
	Handlebars.registerHelper( 'borrowerFullNames', function ( value ) {
		if ( value.length === 0) return '';

		const list = value.map( m => {
			const c = contacts[m.cid];
			return {
				firstname: formatName( c.firstname ),
				lastname: formatName( c.lastname ),
				label: m.label
			}
		} );
		list.sort( ( a, b ) => {
			if ( a.label === 'Primary' ) {
				return -1;		
			}
			if ( b.label === 'Primary' ) {
				return 1;		
			}
			return 0;
		} );
		
		// Group names by last name
		const groups = list.reduce( ( acc, { firstname, lastname } ) => {
			acc[lastname] = acc[lastname] || [];
			acc[lastname].push( firstname );
			return acc;
		}, {} );
		
		// Format each group
		const formattedGroups = Object.entries( groups ).map( ( [ lastname, firstnames ] ) => {
			if ( firstnames.length === 1 ) {
				// Single name in the group
				return `${firstnames[0]} ${lastname}`;
			} else {
				// Multiple names, join all but the last with commas, and add 'and' before the last name
				const allButLast = firstnames.slice( 0, -1 ).join( ', ' );
				const last = firstnames[firstnames.length - 1];
				return `${allButLast} and ${last} ${lastname}`;
			}
		} );

		// Join groups with 'and' for the final output
		if ( formattedGroups.length === 1 ) {
			return formattedGroups[0];
		} else {
			const lastGroup = formattedGroups.pop();
			return `${formattedGroups.join(', ')} and ${lastGroup}`;
		}
	} );
    Handlebars.registerHelper( 'borrowerPreferredFullNames', function ( value ) {
		if ( value.length === 0) return '';

		const list = value.map( m => {
			const c = contacts[m.cid];
			if ( c ) {
				return {
					firstname: formatName( ( c.namePreferred && c.namePreferred.length ) ? c.namePreferred : c.firstname ),
					lastname: formatName( c.lastname ),
					label: m.label
				}
			} else {
				console.log( 'missing: ' + m.firstname );
				return {
					firstname: formatName( m.firstname ),
					lastname: formatName( m.lastname ),
					label: 'Unknown'
				}
			}
		} );
		list.sort( ( a, b ) => {
			if ( a.label === 'Primary' ) {
				return -1;		
			}
			if ( b.label === 'Primary' ) {
				return 1;		
			}
			return 0;
		} );
		
		// Group names by last name
		const groups = list.reduce( ( acc, { firstname, lastname } ) => {
			acc[lastname] = acc[lastname] || [];
			acc[lastname].push(firstname);
			return acc;
		}, {} );
		
		// Format each group
		const formattedGroups = Object.entries( groups ).map( ( [ lastname, firstnames ] ) => {
			if ( firstnames.length === 1 ) {
				// Single name in the group
				return `${firstnames[0]} ${lastname}`;
			} else {
				// Multiple names, join all but the last with commas, and add 'and' before the last name
				const allButLast = firstnames.slice( 0, -1 ).join( ', ' );
				const last = firstnames[firstnames.length - 1];
				return `${allButLast} and ${last} ${lastname}`;
			}
		});

		// Join groups with 'and' for the final output
		if ( formattedGroups.length === 1 ) {
			return formattedGroups[0];
		} else {
			const lastGroup = formattedGroups.pop();
			return `${formattedGroups.join( ', ' )} and ${lastGroup}`;
		}
	} );
	Handlebars.registerHelper( 'borrowerEmails', function ( value ) {
		if ( value.length === 0 ) return '';

		const emails = new Set();
		value.forEach( f => {
			const c = contacts[f.cid];
			if ( c.email && c.email.length ) {
				emails.add( c.email );
			}
		} );
		
		return [ ...emails ].join( ', ' );
	} );
	Handlebars.registerHelper( 'primaryMailingAddress', function ( value ) {
		if ( !value || value.length === 0 ) return '';

		let addr = '';
		const pri = value.find( f => f.label === 'Primary' ) || value[0];
		if ( pri ) {
			const c = contacts[pri.cid];
			if ( c ) {
				if ( c.mailingAddress1 && c.mailingAddress1.length ) {
					addr = c.mailingAddress1;
				} else if ( c.homeAddress1 && c.homeAddress1.length ) {
					addr = c.homeAddress1;
				}
			}
		}
		
		return addr;
	} );
	Handlebars.registerHelper( 'primaryMailingCSZ', function ( value ) {
		if ( !value || value.length === 0 ) return '';

		let csz = '';
		const pri = value.find( f => f.label === 'Primary' ) || value[0];
		if ( pri ) {
			const c = contacts[pri.cid];
			if ( c ) {
				if ( c.mailingCity && c.mailingCity.length ) {
					csz = c.mailingCity;
					if ( c.mailingState && c.mailingState.length ) {
						csz += ', ' + c.mailingState;
					}
					if ( c.mailingZip && c.mailingZip.length ) {
						csz += ' ' + c.mailingZip;
					}
				} else if ( c.homeCity && c.homeCity.length ) {
					csz = c.homeCity;
					if ( c.homeState && c.homeState.length ) {
						csz += ', ' + c.homeState;
					}
					if ( c.homeZip && c.homeZip.length ) {
						csz += ' ' + c.homeZip;
					}
				}
			}
		}
		
		return csz;
	} );
	Handlebars.registerHelper( 'partnerEmails', function ( value ) {
		if ( value.length === 0 ) return '';

		const emails = new Set();
		value.forEach( f => {
			const c = contacts[f.cid];
			if ( c.email && c.email.length ) {
				emails.add( c.email );
			}
			if ( c.emailPartner1 && c.emailPartner1.length ) {
				emails.add( c.emailPartner1 );
			}
			if ( c.emailPartner2 && c.emailPartner2.length ) {
				emails.add( c.emailPartner2 );
			}
		} );
		
		return [ ...emails ].join( ', ' );
	} );
	Handlebars.registerHelper( 'borrowerPhones', function ( value ) {
		if ( value.length === 0 ) return '';

		const phones = new Set();
		value.forEach( f => {
			const c = contacts[f.cid];
			if ( c.phoneMobile && c.phoneMobile.length ) {
				phones.add( c.phoneMobile );
			} else if ( c.phone && c.phone.length ) {
				phones.add( c.phone );
			} else if ( c.phoneWork && c.phoneWork.length ) {
				phones.add( c.phoneWork );
			} else if ( c.phoneHome && c.phoneHome.length ) {
				phones.add( c.phoneHome );
			}
		} );
		
		return [ ...phones ].join( ', ' );
	} );
	Handlebars.registerHelper( 'preferredFirstName', function ( value ) {
		if ( !value || typeof( value ) !== 'object' ) return '';
        const name = ( value.namePreferred && value.namePreferred.length ) ? value.namePreferred : value.firstname;
		return ( name ) ? name : '';
	} );
	Handlebars.registerHelper( 'preferredFullName', function ( value ) {
		if ( !value || typeof( value ) !== 'object' ) return '';
        let name = ( value.namePreferred && value.namePreferred.length ) ? value.namePreferred : value.firstname;
        if ( value.lastname && value.lastname.length ) {
            name += ' ' + value.lastname;
        }
		return ( name ) ? name : '';
	} );
	Handlebars.registerHelper( 'preferredPhone', function ( value ) {
		if ( !value || !value.length ) {
			return '';
		}
		let p = '';
		const c = contacts[value];
		if ( c ) {
			if ( c.phoneMobile && c.phoneMobile.length ) {
				p = c.phoneMobile;
			} else if ( c.phone && c.phone.length ) {
				p = c.phone;
			} else if ( c.phoneWork && c.phoneWork.length ) {
				p = c.phoneWork;
			} else if ( c.phoneHome && c.phoneHome.length ) {
				p = c.phoneHome;
			}
		}
		return p;
	} );
	Handlebars.registerHelper( 'haveHas', function ( value ) {
        return ( value && value.length > 1 ) ? 'have' : 'has';
    } );
	Handlebars.registerHelper( 'aAn', function ( value ) {
		const vowels = "AEIOUaeiou";
		return ( value && vowels.includes( value[0] ) ) ? 'an' : 'a';
    } );
	Handlebars.registerHelper( 'singlePlural', function ( single, plural, bucket ) {
		return ( bucket && bucket.length > 1 ) ? plural : single;
	} );
	Handlebars.registerHelper( 'fullStateName', function ( abbreviation ) {
		const stateMap = {
			AL: "Alabama",
			AK: "Alaska",
			AZ: "Arizona",
			AR: "Arkansas",
			CA: "California",
			CO: "Colorado",
			CT: "Connecticut",
			DE: "Delaware",
			FL: "Florida",
			GA: "Georgia",
			HI: "Hawaii",
			ID: "Idaho",
			IL: "Illinois",
			IN: "Indiana",
			IA: "Iowa",
			KS: "Kansas",
			KY: "Kentucky",
			LA: "Louisiana",
			ME: "Maine",
			MD: "Maryland",
			MA: "Massachusetts",
			MI: "Michigan",
			MN: "Minnesota",
			MS: "Mississippi",
			MO: "Missouri",
			MT: "Montana",
			NE: "Nebraska",
			NV: "Nevada",
			NH: "New Hampshire",
			NJ: "New Jersey",
			NM: "New Mexico",
			NY: "New York",
			NC: "North Carolina",
			ND: "North Dakota",
			OH: "Ohio",
			OK: "Oklahoma",
			OR: "Oregon",
			PA: "Pennsylvania",
			RI: "Rhode Island",
			SC: "South Carolina",
			SD: "South Dakota",
			TN: "Tennessee",
			TX: "Texas",
			UT: "Utah",
			VT: "Vermont",
			VA: "Virginia",
			WA: "Washington",
			WV: "West Virginia",
			WI: "Wisconsin",
			WY: "Wyoming"
		};
		  
		return stateMap[abbreviation] || abbreviation;
	} );
    Handlebars.registerHelper( 'calculateFontSize', function() {
		// Extract options and inputs
		const options = arguments[arguments.length - 1];
		const inputs = Array.from( arguments ).slice( 0, -1 );
	  
		// Get dimensions and margin from options, or use defaults
		const availableWidth = options.hash.width || 792; // Default to 11 inches (792 points)
		const availableHeight = options.hash.height || 612; // Default to 8.5 inches (612 points)
		const margin = options.hash.margin || 72; // Default to 1 inch margin
		const lineSpacing = options.hash.lineSpacing || 1.2; // Default line spacing
	  
		const maxWidth = ( availableWidth - 2 * margin ) * 0.95; // Use 90% of available width
		const maxHeight = ( availableHeight - 2 * margin ) * 0.75; // Use 60% of available height
	  
		const calculateSize = ( text ) => {
			const charCount = text.length;
			
			// Estimate width-based font size (assuming average char width is 0.6 * font size)
			const widthBasedSize = maxWidth / ( charCount * 0.525 );
			
			// Estimate height-based font size
			const heightBasedSize = maxHeight / ( inputs.length * lineSpacing );
			
			// Return the smaller of the two sizes
			return Math.min( widthBasedSize, heightBasedSize );
		};
	  
		// Calculate font size for each input and find the smallest
		const fontSize = Math.min(...inputs.map(calculateSize));
	  
		// Return the font size, rounded down to nearest whole number
		return Math.floor(fontSize);
	} );
	Handlebars.registerHelper( 'array', function() {
		return Array.from( arguments ).slice( 0, -1 ).reduce( ( acc, arg ) => {
			if ( arg !== null && arg !== undefined ) {
				if ( Array.isArray( arg ) ) {
					acc.push( ...arg );
				} else {
					acc.push( arg );
				}
			}
			return acc;
		}, [] );
	} );
	Handlebars.registerHelper( 'accountSignature', function( aid ) {
		if ( !aid ) {
			return '';
		}

		if ( !params || !params.user || !params.user.additionalAccounts ) {
			return '';
		}

		const acct = params.user.additionalAccounts.find( a => a.aid === aid );
		if ( !acct ) {
			return '';
		}

		return acct.signature;
	} );
	Handlebars.registerHelper( 'accountOwner', function( aid ) {
		if ( !aid ) {
			return '';
		}

		if ( !params || !params.user || !params.user.additionalAccounts ) {
			return '';
		}

		const acct = params.user.additionalAccounts.find( a => a.aid === aid );
		if ( !acct ) {
			return '';
		}

		return acct.useSig;
	} );
	Handlebars.registerHelper( 'teamMember', function( uid, field ) {
		if ( !uid || !field ) {
			return '';
		}

		if ( !params || !params.user || !params.user.lists || !params.user.lists.team ) {
			return '';
		}

		const u = params.user.lists.team.find( f => f.uid === uid );
		if ( !u ) {
			return '';
		}

		return u[field];
	} );
	Handlebars.registerHelper( 'splitFullName', function( fullName ) {
		if ( !fullName || !fullName.length ) {
			return null;
		}
	
		// Trim the input to remove any leading/trailing whitespace
		const trimmedName = fullName.trim();
	  
		// Split the string into words
		const words = trimmedName.split(/\s+/);
	  
		// The last word is considered the last name
		let lastname = words.pop();
		if ( lastname.toLowerCase() === 'jr' || lastname.toLowerCase() === 'sr' || lastname.toLowerCase() === 'ii' || lastname.toLowerCase() === 'iii' ) {
			lastname = words.pop() + ' ' + lastname;
		}
	  
		// Everything else is considered the first name
		let firstname = words.join(' ');
		const middleInitialRegex = /\b[A-Z]\.?(\s|$)/g;
		firstname = firstname.replace( middleInitialRegex, ' ' ).trim();
		
		const capitalizeWord = ( word ) => {
			// Check if the word is all lowercase
			if ( /^[a-z]+$/.test( word ) ) {
				return word.charAt( 0 ).toUpperCase() + word.slice( 1 ).toLowerCase();
			}
			// Check if the word is all uppercase
			if ( /^[A-Z]+$/.test( word ) ) {
				return word.charAt( 0 ).toUpperCase() + word.slice( 1 ).toLowerCase();
			}
			return word;
		};
	
		// Capitalize first and last names
		firstname = firstname.split(' ').map( capitalizeWord ).join( ' ' );
		lastname = lastname.split(' ').map( capitalizeWord ).join( ' ' );
		
		console.log( 'returning: ', { firstname, lastname } );
		return {
			firstname,
			lastname
		};
	} );
	Handlebars.registerHelper( 'calcYears', function( months ) {
		const numMonths = parseFloat( months );
	
		if ( !isNaN( numMonths ) ) {
			return ( numMonths / 12 );
		}

		return '';
	} );
	Handlebars.registerHelper( 'formLink', function( aid, fid, oid ) {
		if ( !aid || !fid || !oid ) {
			return null;
		}
		return `https://lp.thirdfloor.ai/${aid}/${fid}/${oid}`;
	} );
	Handlebars.registerHelper( 'inlineHtml', ( rawHtml = '' ) => {
		const stripped = sanitizeHtml( rawHtml, {
			// ① allow only inline tags
			allowedTags: [
				'strong', 'b', 'em', 'i', 'u', 'span', 'a', 'br'
			],
			allowedAttributes: {
				a: ['href', 'target', 'rel'],
				span: ['style']          // optional – styles get inlined later anyway
			},
			// ② turn any <p> that survives into <br>
			transformTags: {
				p: () => ({ tagName: 'br' })
			},
			// ③ drop anything empty
			exclusiveFilter: node =>
				(node.tag === 'br' && !node.text.trim()) ||
				(node.text && !node.text.trim())
		} );
	  
		// sanitize-html leaves the very first <br> if the original string was a single <p>.
		// A quick trim removes it.
		return stripped.replace(/^\s*<br\s*\/?>/i, '').trim();
	} );
	/**
	 * {{rateLookup ratesArray "30yr Fixed"}}                     → latest rate
	 * {{rateLookup ratesArray "30yr Fixed" date="2025-07-18"}}   → rate on a date
	 * {{rateLookup ratesArray "30yr Fixed" field="change"}}      → latest daily-change
	 * {{rateLookup ratesArray "30yr Fixed" date="2025-07-18" field="change"}}
	 */
	Handlebars.registerHelper( 'rateLookup', function ( arr, program, options ) {
		if ( !Array.isArray( arr ) || !program ) return '';
	
		// extract hash parameters (all optional)
		const { date = null, field = 'rate' } = options.hash || {};
	
		// keep only the rows we care about
		let rows = arr.filter(
		( r ) => r.program === program && ( !date || r.date === date )
		);
		if ( rows.length === 0 ) return '';              // nothing found
	
		// if no exact date supplied, pick the most-recent by date
		if ( !date ) {
		rows.sort( ( a, b ) => b.date.localeCompare( a.date ) ); // descending
		}
	
		const value = rows[0][field];                 // 'rate' or 'change'
		return typeof value === 'string' ? value : String( value );
	} );
	/**
	 * {{calcPayment amount rate years}}
	 *
	 * Example:
	 *   {{calcPayment 350000 6.25 30}}   -> 2154.57
	 *   {{calcPayment amount rate term}} -> works with context vars too
	 */
	Handlebars.registerHelper( 'calcPayment', function ( amount, rate, years ) {
		// coerce inputs
		const P = parseFloat( amount );
		let r = parseFloat( rate );
		const n = parseInt( years, 10 ) * 12;      // total monthly periods

		if ( isNaN( P ) || isNaN( r ) || isNaN( n ) || n === 0 ) return 0;

		// allow percent or decimal
		if ( r > 1 ) r = r / 100;
		const m = r / 12;                        // monthly rate

		// handle zero‑interest edge case
		const payment = m === 0
			? P / n
			: ( P * m ) / ( 1 - Math.pow( 1 + m, -n ) );

		// Round to cents
		return Number( payment.toFixed( 0 ) );
	} );
	/**
	 * {{rateMax rates "30yr Jumbo" period="year"}}
	 *    → highest 30‑year‑jumbo rate in the last year
	 *
	 * {{rateMin rates "30yr FHA"}}
	 *    → lowest 30-year-fha rate in the last 7 days
	 * 
	 * {{rateMin rates "30yr VA" period="month" end="2025-07-18"}}
	 * 	  → lowest 30-year-va rate in the MTD
	 */
	Handlebars.registerHelper( 'rateMax', function ( arr, program, options ) {
		return rateExtreme( arr, program, options, Math.max );
	} );
	  
	  // Helper: lowest rate in a window
	Handlebars.registerHelper( 'rateMin', function ( arr, program, options ) {
		return rateExtreme( arr, program, options, Math.min );
	} );
	/**
	 * {{rateScale "6.04%" "6.90%" "6.78%"}}                       → "83%"
	 * {{rateScale min=(rateMin …) max=(rateMax …) current=(rateLookup …) decimals=1}}
	 *                                                               → "82.9%"
	 *
	 * Hash options
	 *   decimals : 0 (default)  – number of digits after the decimal point
	 *   suffix   : "%" (default) – change/omit if you need a raw number
	 */
	Handlebars.registerHelper( 'rateScale', function ( minRate, maxRate, currentRate, options ) {
		const { decimals = 0, suffix = '%' } = options.hash || {};
	
		const min = parseFloat( minRate );
		const max = parseFloat( maxRate );
		const cur = parseFloat( currentRate );
	
		if ( [ min, max, cur ].some( Number.isNaN ) || max === min ) return '';
	
		const pct = ( ( cur - min ) / ( max - min ) ) * 100;
		return pct.toFixed( decimals ) + suffix;
	} );
	Handlebars.registerHelper('yearsSinceLabel', function (d) {
		if (!d) return '';
	
		const start = new Date(d);
		const now = new Date();
	
		const diffMs = now - start;
		const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);
		const years = Math.round(diffYears);
	
		return years === 1 ? '1 year' : `${years} years`;
	} );
	

	  
	return t( { ...params } );
}

const generateRenderParams = ( { obj, objType, contacts, opps, loans, user, extraFields } ) => {
	const accts = user?.lists?.accounts;
	const team = user?.lists?.team;
	const a = accts?.find( f => f.id === obj.aid );
    const owner = ( a ) ? team?.find( f => f.email === a.owner ) : user;
        
    let params = {
		account: a,
        user: user,
        owner: owner,
        extra: extraFields
    };

	if ( objType === 'contact' ) {
		const refs = [];

		if ( obj.priRole === 'partner' ) {
			obj['activeLeads'] = false;
			obj['activePAs'] = false;
			obj['activeLoans'] = false;

			Object.keys( contacts ).forEach( cid => {
				const c = contacts[cid];
				if ( c.referredById === obj.cid ) {
					const los = [];
					const ls = [];

					Object.keys( opps ).forEach( o => {
						const lo = opps[o];
						if ( lo.relationships && lo.relationships.find( f => f.cid === c.cid ) ) {
							const q = a?.crm?.meta?.global?.picklist?.status?.find( f => f.value === lo.status );

							if ( [ 'lead', 'talking', 'app-sent', 'setup', 'review' ].includes( q?.queue ) ) {
								obj['activeLeads'] = true;
								lo['queue'] = 'lead';
								los.push( lo );
							}
						}
					} );

					Object.keys( loans ).forEach( lid => {
						const l = loans[lid];
						if ( l.borrowers && l.borrowers.find( f => f.cid === c.cid ) ) {
							const q = a?.crm?.meta?.loan?.picklist?.status?.find( f => f.value === l.status );

							if ( q?.queue === 'pa' ) {
								obj['activePAs'] = true;
								l['queue'] = 'pa';
								ls.push( l );
							}

							if ( q?.queue === 'active' ) {
								obj['activeLoans'] = true;
								l['queue'] = 'active';
								ls.push( l );
							}
						}
					} );

					refs.push( {
						...c,
						opps: los,
						loans: ls
					} );
				}
			} );
		}

		params['contact'] = {
			...obj,
			referredBy: contacts[obj.referredById],
			referrals: refs
		};
	} else if ( objType === 'loan' ) {
		if ( obj.borrowers?.length > 0 ) {
			const pri = obj.borrowers.find( f => f.label === 'Primary' ) || obj.borrowers[0];
			const c = contacts[pri?.cid];
			if ( c ) {
				params['contact'] = {
					...c,
					referredBy: contacts[c.referredById]
				};
			}
		}
		params['loan'] = {
			...obj,
			buyersAgent: contacts[obj.baId],
			buyersAgentTC: contacts[obj.baTcId],
			listingAgent: contacts[obj.laId],
			listingAgentTC: contacts[obj.laTcId],
			escrow: contacts[obj.escrowOfficerId],
			title: contacts[obj.titleRepId],
			lp2rep: team?.find( f => f.uid === obj.lp2 )
		};
	}

	return params;
}

const getMimeTypeFromExtension = ( extension ) => {
	const cleanExt = extension.replace(/^\./, '').toLowerCase();
  	const extensionToMime = {
		// Images
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		png: 'image/png',
		gif: 'image/gif',
		svg: 'image/svg+xml',
		webp: 'image/webp',
	
		// Documents
		pdf: 'application/pdf',
		doc: 'application/msword',
		docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		xls: 'application/vnd.ms-excel',
		xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		csv: 'text/csv',
		txt: 'text/plain',
	
		// Audio
		mp3: 'audio/mpeg',
		wav: 'audio/wav',
		ogg: 'audio/ogg',
	
		// Video
		mp4: 'video/mp4',
		mov: 'video/quicktime',
		avi: 'video/x-msvideo',
		mkv: 'video/x-matroska',   // Added MKV extension
	
		// Add more as needed...
	};
  
	return extensionToMime[cleanExt] || 'application/octet-stream';
}

function formatDateBucket( date, resolution = 'monthly', ignoreYear = false ) {
	const d = new Date( date );
	if ( isNaN( d ) ) return 'Invalid';
  
	if ( ignoreYear === true ) {
		d.setFullYear( (new Date()).getFullYear() );
	}
	
	const year = d.getFullYear();
	const month = `${d.getMonth() + 1}`.padStart(2, '0');
	const day = `${d.getDate()}`.padStart(2, '0');
  
	switch (resolution) {
		case 'daily':
			return `${year}-${month}-${day}`;
		case 'weekly': {
			const weekStart = new Date( d );
			weekStart.setDate( d.getDate() - d.getDay() ); // Sunday
			const y = weekStart.getFullYear();
			const m = `${weekStart.getMonth() + 1}`.padStart(2, '0');
			const dd = `${weekStart.getDate()}`.padStart(2, '0');
			return `${y}-${m}-${dd}`;
		}
		case 'monthly':
		default:
			return `${year}-${month}`;
	}
}

function generateDateBuckets( startDate, endDate, resolution ) {
    const buckets = [];
    const current = new Date( startDate );

	if ( resolution === 'weekly' ) {
        // Move current date back to the previous Sunday (or same day if Sunday)
		console.log( 'incoming:', startDate, endDate );
		console.log( 'weekly reset to sunday:', current.getDate(), current.getDay() );
        current.setDate( current.getDate() - current.getDay() );
		console.log( 'weekly reset now:', current.getDate(), current.getDay() );
    }

    while ( true ) {
		const label = formatDateBucket( current, resolution );
		buckets.push( label );

        if ( resolution === 'daily' ) {
            current.setDate( current.getDate() + 1 );
            if ( current > endDate ) break;
        } else if ( resolution === 'weekly' ) {
			current.setDate( current.getDate() + 7 );
            if ( current > endDate ) break;
        } else if ( resolution === 'monthly' ) {
            current.setMonth( current.getMonth() + 1 );

            // Break if we’ve moved beyond the year+month of the endDate
            const currentYM = formatDateBucket( current, 'monthly');
            const endYM = formatDateBucket( endDate, 'monthly');
            if ( currentYM > endYM ) break;
        }
    }

    return buckets;
}

function parseCurrencyToNumber( val ) {
	if (typeof val === 'number') return val;
	if (typeof val === 'string') {
	  	return parseFloat( val.replace( /[^0-9.]/g, '' ) ) || 0;
	}
	return 0;
}

function transformBreakdownToDonutData( breakdownObj, mode = 'units' ) {
	if (!breakdownObj) return [];

  return Object.entries(breakdownObj).map(([label, values]) => ({
    id: label,
    label,
    value: values[mode],
  }));
}

function getAutoResolution( startDate, endDate ) {
    const msPerDay = 1000 * 60 * 60 * 24;
    const diffInDays = Math.round( ( endDate - startDate ) / msPerDay );

    if ( diffInDays < 42 ) return 'daily';
    if ( diffInDays <= 180 ) return 'weekly';
    return 'monthly';
}

export { render, generateRenderParams, generatePdf, isToday, isTomorrow, isThisWeek, isNextWeek, isThisMonth, isNextMonth, formatShorthand, formatCurrencyShort, formatName, formatPhone, formatDate, formatTime, singleSelectQuickFilter, storeMedia, createId, getObjectName, happeningToday, happeningTomorrow, happeningWeek, launchPhoneBurner, stateAbbreviations, monthsShort, monthsLong, chartColors, getExtensionFromMimeType, storeEmailImage, base64ToBlob, extractImages, getMimeTypeFromExtension, formatDateBucket, parseCurrencyToNumber, transformBreakdownToDonutData, generateDateBuckets, getAutoResolution };